/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React from 'react';
import { UserRound, Users } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';

/**
 * FamilyMemberPicker — booking-wizard sub-step that asks "per chi?".
 *
 * Only shown to patients who have an active family bundle (`has_family=true`)
 * and have added at least one family member. The bundle owner ("self") is
 * always the first choice.
 *
 * The component is intentionally stateless — the parent wizard owns the
 * selected key so it can bail out early if the picker is not applicable.
 */
export const FamilyMemberPicker = ({ familyData, selectedKey, onSelect, testId = 'family-picker' }) => {
  if (!familyData?.has_family) return null;
  const members = familyData.members || [];
  if (members.length === 0) return null;

  const self = familyData.self || { key: 'self', full_name: 'Io', relation: 'Titolare' };
  const options = [{ key: 'self', ...self }, ...members];

  return (
    <div className="space-y-3" data-testid={testId}>
      <div className="flex items-center gap-2">
        <Users className="h-4 w-4 text-primary" />
        <h3 className="font-semibold text-sm">Per chi è questa visita?</h3>
        {familyData.discount_pct ? (
          <Badge variant="success" className="ml-auto text-[10px]">
            Bundle -{familyData.discount_pct}%
          </Badge>
        ) : null}
      </div>
      <p className="text-xs text-muted-foreground -mt-1">
        L'appuntamento verrà collegato automaticamente al familiare selezionato.
      </p>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {options.map((m) => {
          const active = (selectedKey || 'self') === m.key;
          return (
            <button
              key={m.key}
              type="button"
              onClick={() => onSelect(m.key === 'self' ? null : m.key)}
              className={cn(
                'group relative rounded-lg border p-3 text-left transition-all',
                'hover:border-primary/60 hover:shadow-elev-sm',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1',
                active
                  ? 'border-primary bg-primary/5 shadow-elev-sm'
                  : 'border-border bg-card',
              )}
              data-testid={`${testId}-option-${m.key}`}
              aria-pressed={active}
            >
              <div className="flex items-center gap-2">
                <div className={cn(
                  'h-8 w-8 rounded-full flex items-center justify-center',
                  active ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground',
                )}>
                  <UserRound className="h-4 w-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-sm truncate">
                    {m.full_name || 'Familiare'}
                  </div>
                  {m.relation && (
                    <div className="text-[11px] text-muted-foreground truncate">
                      {m.relation}
                    </div>
                  )}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default FamilyMemberPicker;
